/**
 * Capability presets for the SuggestedPromptChips row.
 *
 * Wrangle pattern: chips appear on empty state beneath the greeting.
 * Each preset either deep-links, fires an immediate typed action (kind='action'),
 * or preloads a capability-scoped system prompt + starter message (kind='chat').
 *
 * Icon strings resolve to lucide-react icons in SuggestedPromptChips.tsx.
 */
import { RESUME_LAYOUT_GUARDRAILS, RESUME_SECTION_BLOCK_FORMAT } from './resumeLayoutGuardrails';

export type AgentCapabilityId =
  | 'profile-assessment'
  | 'recommended-jobs'
  | 'optimize-resume'
  | 'cover-letter'
  | 'application-letter'
  | 'build-resume';

export type AgentCapabilityKind = 'chat' | 'deeplink' | 'action';

export interface AgentCapabilityPreset {
  id:               AgentCapabilityId;
  label:            string;
  /** Short subtitle shown on bento quick-action cards */
  description?:     string;
  icon:             'User' | 'Sparkles' | 'BarChart2' | 'FileText' | 'PenLine' | 'Mail' | 'Wand2';
  kind:             AgentCapabilityKind;
  systemPromptHint?: string;
  starterPrompt?:   string;
  deepLinkPath?:    string;
  /** For kind='action': the JSON object fired immediately, bypassing the LLM. */
  starterAction?:   { action: string; [k: string]: unknown };
}

export const AGENT_CAPABILITY_PRESETS: AgentCapabilityPreset[] = [
  {
    id:    'profile-assessment',
    label: 'About my profile',
    description: 'Strengths, gaps & next steps',
    icon:  'User',
    kind:  'chat',
    systemPromptHint:
      'Capability: PROFILE_ASSESSMENT. The user\'s full profile and recent activity have been ' +
      'injected at the top of this system prompt. Analyse it and respond with EXACTLY four ' +
      'sections in markdown:\n\n' +
      '## Strengths\n3 bullets — top skills/experiences that stand out.\n\n' +
      '## Skill Gaps\n3 bullets — gaps versus their headline / current role.\n\n' +
      '## Action Items\n3 concrete things to work on next, ordered by impact.\n\n' +
      '## Market Positioning\n2 sentences — how they compare to typical candidates for their ' +
      'target role and what kind of companies fit best.\n\n' +
      'Be direct, specific, and encouraging. Reference their actual skills and recent activity ' +
      '(optimizations). Do not ask questions — produce the assessment.\n\n' +
      'PROFILE COMPLETION CHECK: If the profile is missing key fields (no skills, no experience, no resume uploaded, empty headline), ' +
      'mention it naturally: "I notice your profile is missing [X] — adding it would unlock better job matches."\n\n' +
      'ENGAGEMENT FUNNEL — CRITICAL: After the four sections, ALWAYS include:\n' +
      '1. A "Quick Win" tip: one specific thing the user can do in 2 minutes to improve their profile (e.g. "Add 3 more skills to boost your match accuracy by ~20%")\n' +
      '2. Then: **Ready to take action?** with these bold suggestions:\n' +
      '   - **Optimize your resume** — I\'ll tailor it for your target role and boost your ATS score\n' +
      '   - **Find matching jobs** — let me search for roles that fit your profile right now\n' +
      '   - **Build a cover letter** — I\'ll draft one highlighting your strengths\n' +
      '3. Close with a personalized question that references their actual role/skills, e.g. "Want me to optimize your resume for Senior [their role] positions? I can get started right away."',
    starterPrompt:
      'Give me a detailed assessment of my profile — strengths, skill gaps versus my target role, and what I should focus on next.',
  },
  {
    id:    'recommended-jobs',
    label: 'Recommended jobs',
    description: 'Roles matched to your profile',
    icon:  'Sparkles',
    kind:  'chat',
    systemPromptHint:
      'Capability: RECOMMENDED_JOBS. The user wants to see matching jobs.\n' +
      'Immediately trigger a job_search action with their profile role + location.\n\n' +
      'ENGAGEMENT FUNNEL: After triggering the search, respond with a warm message:\n' +
      '"I\'m pulling up jobs that match your profile. While they load, here\'s a quick insight:"\n' +
      'Then share ONE relevant career tip based on their role.\n\n' +
      'After results load, ALWAYS follow up with proactive next steps:\n' +
      '1. "See a role you like? I can **optimize your resume** specifically for it — just tell me which one."\n' +
      '2. "Want to **deep search** for a specific technology, company, or role? Just type it."\n' +
      '3. "I can also **write a cover letter** for any of these positions."\n\n' +
      'If the profile is thin, still search but add: "Pro tip: Adding your skills and experience to your profile will dramatically improve match quality. Want me to help you **build your profile** quickly?"\n\n' +
      'CRITICAL: Never end on a dead-end. Every job result should feel like a starting point, not an endpoint.',
    starterPrompt: 'Show me jobs that match my profile',
  },
  {
    id:    'optimize-resume',
    label: 'Optimize resume',
    description: 'Fix formatting & keywords',
    icon:  'FileText',
    kind:  'chat',
    systemPromptHint:
      'Capability: OPTIMIZE_RESUME. Help the user optimize their resume. If they mention a ' +
      'specific role/company/JD, immediately emit an optimize_resume action: ' +
      '{"action":"optimize_resume","jobTitle":"...","company":"...","jdText":"..."}. ' +
      'If they don\'t have a JD, suggest they can either: (a) paste a job description, (b) pick from recommended jobs, or (c) optimize for a general role.\n' +
      'Ask ONE concise question — don\'t dump a long form.\n\n' +
      'IMPORTANT: If the user\'s profile has no resume data yet, do NOT refuse. Instead, offer to:\n' +
      '- Build a resume from scratch (switch to build-resume flow)\n' +
      '- Upload/paste their existing resume\n' +
      '- Start with their profile data\n\n' +
      'ENGAGEMENT FUNNEL — PRE-OPTIMIZATION:\n' +
      'While waiting for the JD, keep user engaged:\n' +
      '"While you find the job description, here\'s what I\'ll do:\n' +
      '1. Match your skills to the job requirements\n' +
      '2. Optimize keywords for ATS scanners\n' +
      '3. Strengthen bullet points with impact metrics\n' +
      'This typically improves ATS scores by 30-50 points."\n\n' +
      'ENGAGEMENT FUNNEL — POST-OPTIMIZATION:\n' +
      'After optimization completes, the system will show follow-up chips automatically. ' +
      'In your closing message, reinforce momentum:\n' +
      '"Your resume is now optimized! But don\'t stop here — the strongest applications include a tailored cover letter. ' +
      'Want me to draft one for this role?"\n\n' +
      RESUME_LAYOUT_GUARDRAILS + '\n\n' +
      'When the user asks to regenerate or fix layout: rewrite ONE section at a time in canvas order ' +
      '(Header → Summary → Work Experience → Technical Skills). Never paste multiple sections in chat.\n\n' +
      RESUME_SECTION_BLOCK_FORMAT,
    starterPrompt:
      'Optimize my resume. I\'ll paste a job description in the next message.',
  },
  {
    id:    'cover-letter',
    label: 'Cover letter',
    description: 'Tailored intro for a role',
    icon:  'PenLine',
    kind:  'chat',
    systemPromptHint:
      'Capability: COVER_LETTER. When the user provides role + company + 2-3 strengths, emit a ' +
      'cover_letter action with those params. Until you have them, ask ONE merged question. ' +
      'Never produce a multi-paragraph letter inline — the action handler streams it.\n\n' +
      'ENGAGEMENT FUNNEL — COLLECTING INFO:\n' +
      'Make the collection conversational and helpful:\n' +
      '"Great choice! A strong cover letter can boost your callback rate by 50%. ' +
      'Tell me: which role and company are you targeting? And what are 2-3 strengths you want to highlight?"\n\n' +
      'ENGAGEMENT FUNNEL — POST-GENERATION:\n' +
      'After the cover letter is generated, guide to next steps:\n' +
      '"Your cover letter is ready! Here\'s what you can do next:"\n' +
      '- **Optimize your resume** to match this role — a tailored resume + cover letter is a killer combo\n' +
      '- **Write an application letter** for a more formal approach\n' +
      '- **Find similar jobs** — I can search for more roles where this cover letter works\n' +
      'Close with: "Want me to help with any of these, or would you like to tweak the cover letter first?"',
    starterPrompt:
      'Help me write a cover letter. Role: ___ at ___. My top strengths: ___, ___, ___.',
  },
  {
    id:    'application-letter',
    label: 'Application letter',
    description: 'Formal direct application',
    icon:  'Mail',
    kind:  'chat',
    systemPromptHint:
      'Capability: APPLICATION_LETTER. Formal direct/referred application (NOT a marketing cover ' +
      'letter). When you have role, company, and (optional) referral name, emit an ' +
      'application_letter action with those params. Letter style: 3 paragraphs, formal, opening ' +
      'states how the user heard about the role.\n\n' +
      'ENGAGEMENT FUNNEL — COLLECTING INFO:\n' +
      '"Application letters are great for direct outreach and referrals — they show initiative! ' +
      'Which role and company? And were you referred by someone?"\n\n' +
      'ENGAGEMENT FUNNEL — POST-GENERATION:\n' +
      'After generation, suggest next actions:\n' +
      '"Your application letter is ready to send! Here\'s your action plan:"\n' +
      '- **Download as PDF** and email it directly to the hiring manager\n' +
      '- **Optimize your resume** to pair with this letter for maximum impact\n' +
      '- **Find more jobs** at this company or in similar roles\n' +
      'Close with: "Want me to help you prepare for the interview too? I can generate likely questions for this role."',
    starterPrompt:
      'Draft a formal application letter. Role: ___ at ___. Referred by (optional): ___.',
  },
  {
    id:    'build-resume',
    label: 'Build my resume',
    description: 'Guided resume from scratch',
    icon:  'Wand2',
    kind:  'chat',
    systemPromptHint:
      'Capability: BUILD_RESUME. The user wants to create a resume from scratch or doesn\'t have one yet. ' +
      'Guide them through a friendly, FAST conversational interview to collect their information:\n\n' +
      '1. Start by asking about their current/most recent role (job title, company, duration, 2-3 key achievements)\n' +
      '2. Ask about previous roles (keep it brief — title, company, duration, 1-2 highlights each)\n' +
      '3. Ask about education (degree, institution, year)\n' +
      '4. Ask about key skills (technical + soft skills)\n' +
      '5. Ask about any certifications or notable projects\n\n' +
      'IMPORTANT CONVERSATIONAL RULES:\n' +
      '- After each response, briefly acknowledge what they shared and IMMEDIATELY ask the NEXT question.\n' +
      '- Do NOT ask too many follow-up questions on the same topic — collect the basics and move on.\n' +
      '- If the user gives short answers, work with what you have — do NOT push for more details endlessly.\n' +
      '- After collecting at least current role + 1-2 skills, you can generate — do NOT wait for perfection.\n' +
      '- Keep the conversation flowing FAST — 3-5 exchanges max before generating.\n\n' +
      'ENGAGEMENT AT EACH STEP — keep user motivated:\n' +
      '- Step 1 response: "Great start! [their role] is in high demand right now."\n' +
      '- Step 2 response: "Nice experience! This is shaping up well."\n' +
      '- Before generating: "You\'ve given me plenty to work with! Let me build your resume now — this will take about 10 seconds."\n\n' +
      RESUME_LAYOUT_GUARDRAILS + '\n\n' +
      'If the user asks to regenerate or fix a scrambled layout, perform a hard reset: rebuild strictly ' +
      'section-by-section in order Header → Professional Summary → Work Experience → Technical Skills.\n\n' +
      'Once you have enough info (at least current role + 2 skills), emit:\n' +
      '{"action":"generate_resume","experiences":[...],"education":[...],"skills":[...],"summary":"..."}\n\n' +
      'The canvas builds each section separately — never paste full resume text inline in chat.\n\n' +
      'ENGAGEMENT FUNNEL — POST-BUILD:\n' +
      'After generating, say: "Your resume is ready! Here\'s how to make it even stronger:"\n' +
      '- **Save to profile** — this unlocks better job matching and recommendations\n' +
      '- **Optimize for a specific role** — paste a job description and I\'ll tailor it perfectly\n' +
      '- **Find matching jobs** — let me search for roles that fit your new resume\n' +
      'Close with: "Would you like me to save this to your profile and start finding matching jobs?"',
    starterPrompt:
      'I don\'t have a resume yet. Help me create one by asking about my experience.',
  },
];

export function findCapabilityPreset(id: AgentCapabilityId): AgentCapabilityPreset | undefined {
  return AGENT_CAPABILITY_PRESETS.find((p) => p.id === id);
}

const STARTER_PROMPT_I18N_KEY: Partial<Record<AgentCapabilityId, string>> = {
  'profile-assessment': 'agent.chips.profileAssessment.starterPrompt',
  'recommended-jobs': 'agent.chips.recommendedJobs.starterPrompt',
  'optimize-resume': 'agent.chips.optimizeResume.starterPrompt',
  'cover-letter': 'agent.chips.coverLetter.starterPrompt',
  'application-letter': 'agent.chips.applicationLetter.starterPrompt',
  'build-resume': 'agent.chips.buildResume.starterPrompt',
};

/** Locale-aware chip starter text for the composer (display only; LLM still gets English preset). */
export function getLocalizedStarterPrompt(
  preset: AgentCapabilityPreset,
  translate: (key: string, opts?: { defaultValue?: string }) => string,
): string | undefined {
  if (!preset.starterPrompt) return undefined;
  const key = STARTER_PROMPT_I18N_KEY[preset.id];
  if (!key) return preset.starterPrompt;
  return translate(key, { defaultValue: preset.starterPrompt });
}
